<template lang="pug">
AppModal(:value="value" @input="onInput" title="Generar preliquidaciones")
  .generate-modal-content
    p.mb-3
      | Genera la preliquidación diaria (snapshot) para uno o varios conductores
      | en la fecha indicada. El sistema consolidará los conteos y envíos del día.

    AppField(
      label="Fecha"
      :required="true"
      :state="date ? true : (attempted ? false : null)"
      :invalidFeedback="!date && attempted ? 'Selecciona una fecha' : ''"
    )
      AppInput(
        type="date"
        v-model="date"
        :max="today"
      )

    AppField(
      label="Conductores"
      :required="true"
      :state="selectedDriverIds.length > 0 ? true : (attempted ? false : null)"
      :invalidFeedback="selectedDriverIds.length === 0 && attempted ? 'Selecciona al menos un conductor' : ''"
    )
      template(#description)
        small.text-muted
          | {{ selectedDriverIds.length }} seleccionado(s)
      .d-flex.gap-2.mb-2
        AppButton(outline size="sm" @click="selectAll") Seleccionar todos
        AppButton(outline size="sm" @click="clearAll" :disabled="selectedDriverIds.length === 0") Limpiar
      .drivers-list
        b-form-checkbox-group(
          v-model="selectedDriverIds"
          :options="driverCheckboxOptions"
          stacked
          name="drivers"
        )

    .alert.alert-danger(v-if="errorMsg") {{ errorMsg }}
    .alert.alert-success(v-if="successMsg") {{ successMsg }}

  template(#footer)
    AppButton(outline @click="close" class="mr-2") Cancelar
    AppButton(
      :disabled="!canGenerate"
      @click="handleGenerate"
    )
      span(v-if="loading")
        b-spinner.small.mr-1
        | Generando...
      span(v-else) Generar
</template>

<script>
import AppModal from '@/components/UI/AppModal.vue'
import AppButton from '@/components/UI/AppButton.vue'
import AppInput from '@/components/UI/AppInput.vue'
import AppField from '@/components/UI/AppField.vue'
import { mapActions } from 'vuex'

export default {
  name: 'ModalGenerateSettlements',
  components: { AppModal, AppButton, AppInput, AppField },
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      date: today,
      selectedDriverIds: [],
      drivers: [],
      loadingDrivers: false,
      loading: false,
      attempted: false,
      errorMsg: '',
      successMsg: '',
      today
    }
  },
  computed: {
    driverCheckboxOptions() {
      return this.drivers.map(driver => ({
        value: driver.id,
        text: `${driver.name} ${driver.lastName || ''} (${driver.codeDriver || '-'})`.trim()
      }))
    },
    canGenerate() {
      return !this.loading && !!this.date && this.selectedDriverIds.length > 0
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.reset()
        this.loadDrivers()
      }
    }
  },
  methods: {
    ...mapActions('settlement', ['generateSettlements']),
    ...mapActions(['getDrivers']),

    async loadDrivers() {
      this.loadingDrivers = true
      try {
        const drivers = await this.getDrivers()
        this.drivers = Array.isArray(drivers) ? drivers : []
      } catch (err) {
        this.errorMsg = 'No se pudieron cargar los conductores.'
        this.drivers = []
      } finally {
        this.loadingDrivers = false
      }
    },

    selectAll() {
      this.selectedDriverIds = this.drivers.map(d => d.id)
    },
    clearAll() {
      this.selectedDriverIds = []
    },

    reset() {
      this.date = this.today
      this.selectedDriverIds = []
      this.loading = false
      this.attempted = false
      this.errorMsg = ''
      this.successMsg = ''
    },

    onInput(val) {
      this.$emit('input', val)
    },

    close() {
      this.$emit('input', false)
    },

    async handleGenerate() {
      this.attempted = true
      this.errorMsg = ''
      this.successMsg = ''

      if (!this.canGenerate) return

      this.loading = true
      try {
        const payload = {
          date: this.date,
          driverIds: [...this.selectedDriverIds]
        }
        const response = await this.generateSettlements({ data: payload })

        if (response && response.status === 201) {
          const count = Array.isArray(response.data) ? response.data.length : (response.data?.created ?? 1)
          this.successMsg = `Se generaron ${count} preliquidación(es) correctamente.`
          this.$emit('success')
          setTimeout(() => this.close(), 1500)
        } else {
          const data = response?.data
          this.errorMsg = data?.message || 'No se pudieron generar las preliquidaciones.'
        }
      } catch (err) {
        this.errorMsg = err?.response?.data?.message || 'Error al generar las preliquidaciones.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.drivers-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid var(--app-border-color, #e5e7eb);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  background: #fff;
}
</style>